import userService from '../services/user';
import { createVoteThunks } from './voteThunks';
import { createReactionThunk } from './reactionThunks';
import mergeResultsById from '../utils/mergeResultsById';
import type { PostItem, UserPageState, ReduxAction, VotePayload } from '../types/common';
import type { AppDispatch } from '../store';

const userPageReducer = (
  state: UserPageState | null = null,
  action: ReduxAction<unknown>
): UserPageState | null => {
  switch (action.type) {
    case 'FETCH_USER':
      return action.payload as UserPageState;
    case 'TOGGLE_USERPAGE_VOTE':
    case 'TOGGLE_USERPAGE_REACTION':
      return {
        ...state,
        posts: {
          ...(state?.posts || {}),
          results: (state?.posts?.results || []).map((r) =>
            r.id !== (action.payload as VotePayload).id
              ? r
              : { ...r, ...((action.payload as VotePayload).data || {}) }
          ),
        },
      } as UserPageState;
    case 'LOAD_USER_POSTS':
      return {
        ...state,
        posts: {
          ...((action.payload as UserPageState).posts || {}),
          results: [
            ...(state?.posts?.results || []),
            ...(((action.payload as UserPageState).posts?.results as unknown[]) || []),
          ],
        },
      } as UserPageState;
    case 'REFRESH_USER_POSTS':
      // Silent "returning" refresh: in-place field merge only, so a restored
      // scroll position cannot move (see mergeResultsById).
      return {
        ...state,
        posts: {
          ...(state?.posts || {}),
          results: mergeResultsById(
            (state?.posts?.results || []) as PostItem[],
            (((action.payload as UserPageState).posts?.results as PostItem[]) || [])
          ),
        },
      } as UserPageState;
    default:
      return state;
  }
};

export const fetchUser = (username: string) => {
  return async (dispatch: AppDispatch) => {
    const user = await userService.getUser(username, 5, 1);

    dispatch({
      type: 'FETCH_USER',
      payload: user,
    });
  };
};

export const loadUserPosts = (username: string, page: number) => {
  return async (dispatch: AppDispatch) => {
    const user = await userService.getUser(username, 5, page);

    dispatch({
      type: 'LOAD_USER_POSTS',
      payload: user,
    });
  };
};

/** Background page-1 refresh for the returning fast path (see REFRESH_USER_POSTS). */
export const refreshUserPosts = (username: string) => {
  return async (dispatch: AppDispatch) => {
    const user = await userService.getUser(username, 5, 1);

    dispatch({
      type: 'REFRESH_USER_POSTS',
      payload: user,
    });
  };
};

export const { toggleUpvote, toggleDownvote } = createVoteThunks('TOGGLE_USERPAGE_VOTE');
export const toggleReaction = createReactionThunk('TOGGLE_USERPAGE_REACTION');

export default userPageReducer;